import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Checkbox } from "../ui/checkbox";

import Happy from "./Happy.png";
import Neutral from "./Neutral.png";
import Unhappy from "./Unhappy.png";

import { hooks } from "botframework-webchat-component";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  buildEncryptedCredentialBody,
  buildApiHeaders,
  buildApiUrl,
  getQueryBootstrap,
} from "../../utils/apiBootstrap";

const { useSendEvent } = hooks;

const HappinessCard = ({setScript,id,surveyData,disableSend,triggeredby}) => {

  const sendEvent = useSendEvent();
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState("");
  const [readOnly, setReadOnly] = useState(false);
  const [dynamicQuestionText, setDynamicQuestionText] = useState("");
  const [dynamicOptions, setDynamicOptions] = useState(null);

  // NO hardcoded options - everything comes from blob

  const handleSubmit = (feedback) => {
    setSelectedFeedback(feedback);
    setExpanded(true);

    // Update the script after feedback is selected
    let visibleScript = `For the Happiness Meter, you selected ${feedback}. ${dynamicQuestionText || "Please wait..."} Please select from the following options:`;

    setScript(visibleScript); // Set the updated script
  };

  const FormSchema = z.object({
    items: z.array(z.string()).refine((value) => value.some((item) => item), {
      message: "You have to select at least one item.",
    }),
  });

  const form = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      items: [],
    },
  });
  useEffect(() => {
    // Load dynamic data directly from Azure Cosmos DB blob
    const loadDynamicDataFromBlob = async () => {
      try {
        console.log('HappinessCard - Loading dynamic data directly from Cosmos DB blob...');
        const { apiBaseUrl, chatId } = getQueryBootstrap();
        
        // Direct API call to your backend endpoint that reads from Cosmos DB
        const response = await fetch(buildApiUrl(apiBaseUrl, '/survey-settings'), {
          method: 'POST',
          headers: buildApiHeaders({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(
            buildEncryptedCredentialBody(chatId, {
              channel: 'web',
              language: 'en'
            })
          )
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('HappinessCard - Received data from blob:', data);
          
          if (data.secondQuestionText) {
            setDynamicQuestionText(data.secondQuestionText);
          }
          
          if (data.options && data.options.length > 0) {
            const dynamicOptionsFormatted = data.options.map((option, index) => ({
              id: (index + 1).toString(),
              label: option
            }));
            
            setDynamicOptions({
              happy: dynamicOptionsFormatted,
              neutral: dynamicOptionsFormatted,
              unhappy: dynamicOptionsFormatted,
            });
          }
        } else {
          console.error('Failed to load survey settings from blob');
        }
      } catch (error) {
        console.error('Error loading dynamic data from blob:', error);
      }
    };

    // Load data when component first mounts
    if (!dynamicQuestionText && !dynamicOptions) {
      loadDynamicDataFromBlob();
    }

    if (surveyData) {
      const { feedback, comments, selectedOptions } = surveyData;

      setSelectedFeedback(feedback);
      setComments(comments);
      setExpanded(true);

      form.setValue("items", selectedOptions);
      setReadOnly(true);

      // Extract dynamic survey data
      if (surveyData.dynamicData) {
        if (surveyData.dynamicData.secondQuestionText) {
          setDynamicQuestionText(surveyData.dynamicData.secondQuestionText);
        }
        if (surveyData.dynamicData.options && surveyData.dynamicData.options.length > 0) {
          // Convert array of strings to feedbackOptions format
          const dynamicOptionsFormatted = surveyData.dynamicData.options.map((option, index) => ({
            id: (index + 1).toString(),
            label: option
          }));
          
          setDynamicOptions({
            happy: dynamicOptionsFormatted,
            neutral: dynamicOptionsFormatted,
            unhappy: dynamicOptionsFormatted,
          });
        }
      }
    }
  }, [id, form,surveyData]);

  function handleFinish() {
    const formData = form.getValues();
    const dataToSubmit = {
      feedback: selectedFeedback,
      selectedOptions: formData.items,
      comments,
    };
    const senddata = {
      id:id,
data:dataToSubmit,
triggeredby:triggeredby
    }

    console.log("HappinessCard - Submitting Data: ", dataToSubmit);
    sendEvent("submitFeedback", senddata);
    setReadOnly(true);
  }

  const isMobile = window.innerWidth <= 1000; // Check if the screen size is mobile

  // Build script dynamically as text visibility changes
  useEffect(() => {
    let visibleText = `Happiness Meter: How was your experience with Rammas? Happy. Neutral . Unhappy.`;
    if (readOnly){
      visibleText = `For the happiness meter where the options are happy, neutral and, unhappy, You have selected ${selectedFeedback}.`;
      if (selectedFeedback && form.getValues("items").length > 0) {
        const selectedIds = form.getValues("items")
        const currentOptions = dynamicOptions && dynamicOptions[selectedFeedback] ? dynamicOptions[selectedFeedback] : [];
        const selectedLabels = selectedIds.map(id => {
          const option = currentOptions.find(item => item.id === id);
          return option ? option.label : null;
        }).filter(Boolean);

        visibleText += ` You selected the following options: ${selectedLabels.join(", ")}.`;
      }
      if (comments){
        visibleText += ` You have shared comments: "${comments}".... Click to hide comment`;
      }
      visibleText+= 'Thank you for your contribution'
    }else{
    if (selectedFeedback){
      visibleText = `For the happiness meter where the options are happy, neutral and, unhappy, You have selected ${selectedFeedback}.`;

    }

    // Add feedback options to the script if available
    if (selectedFeedback && form.getValues("items").length > 0) {
      const selectedIds = form.getValues("items")
      const currentOptions = dynamicOptions && dynamicOptions[selectedFeedback] ? dynamicOptions[selectedFeedback] : [];
      const selectedLabels = selectedIds.map(id => {
        const option = currentOptions.find(item => item.id === id);
        return option ? option.label : null;
      }).filter(Boolean);

      visibleText += ` You selected the following options: ${selectedLabels.join(", ")}.`;
    }
    if (selectedFeedback && form.getValues("items").length == 0){
      const currentOptions = dynamicOptions && dynamicOptions[selectedFeedback] ? dynamicOptions[selectedFeedback] : [];
      console.log('Available Options', currentOptions)
      const labels = currentOptions.map(option => option.label);

      const allLabels = labels.join(", ");
      visibleText += `${dynamicQuestionText || "Loading..."} please select from the following options  ${allLabels} `
    }
    if (showComments&& comments=="" ){
      visibleText += `Would you like to provide further comments? Please enter your comment".... Click to hide comment`
    }
    if (showComments && comments) {
      visibleText += ` You have shared comments: "${comments}".... Click to hide comment`;
    }
    if(selectedFeedback){
      if (!showComments){
        visibleText += `...Click to Share Comments`;

      }
      visibleText+=' ...Click to Finish'
    }
  }

    setScript(visibleText); // Update the script
    console.log("HappinessCard - Script Updated: ", visibleText); // Log the script change

  }, [selectedFeedback, showComments,form.getValues("items"), comments,readOnly]);

  // Log when the card is shown and initialized


  return (
    <Card className="border-none max-w-sm">
      <CardHeader>
        <CardTitle>Happiness Meter</CardTitle>
        <CardDescription>How was your experience with Rammas?</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-6">
        <div className="grid grid-cols-3 gap-2 items-center">
          <div className="text-center flex flex-col items-center justify-center">
            <Button
              onClick={() => handleSubmit("happy")}
              className={`p-0 bg-transparent hover:bg-transparent border-0 ${
                isMobile ? "w-[4rem] h-[4rem]" : "w-24 h-24"
              }`}
              disabled={readOnly}
            >
              <img
                src={Happy}
                alt="Happy"
                className={`object-contain filter ${
                  isMobile ? "w-[3rem] h-[3rem]" : "w-16 h-16"
                } ${
                  selectedFeedback === "happy"
                    ? "grayscale-0 contrast-100"
                    : "grayscale contrast-0 hover:grayscale-0 hover:contrast-100"
                } transition-all duration-300`}
              />
            </Button>
            <p className="mt-2 text-sm">Happy</p>
          </div>

          <div className="text-center flex flex-col items-center justify-center">
            <Button
              onClick={() => handleSubmit("neutral")}
              className={`p-0 bg-transparent hover:bg-transparent border-0 ${
                isMobile ? "w-[4rem] h-[4rem]" : "w-24 h-24"
              }`}
              disabled={readOnly}
            >
              <img
                src={Neutral}
                alt="Neutral"
                className={`object-contain filter ${
                  isMobile ? "w-[3rem] h-[3rem]" : "w-16 h-16"
                } ${
                  selectedFeedback === "neutral"
                    ? "grayscale-0 contrast-100"
                    : "grayscale contrast-0 hover:grayscale-0 hover:contrast-100"
                } transition-all duration-300`}
              />
            </Button>
            <p className="mt-2 text-sm">Neutral</p>
          </div>

          <div className="text-center flex flex-col items-center justify-center">
            <Button
              onClick={() => handleSubmit("unhappy")}
              className={`p-0 bg-transparent hover:bg-transparent border-0 ${
                isMobile ? "w-[4rem] h-[4rem]" : "w-24 h-24"
              }`}
              disabled={readOnly}
            >
              <img
                src={Unhappy}
                alt="Unhappy"
                className={`object-contain filter ${
                  isMobile ? "w-[3rem] h-[3rem]" : "w-16 h-16"
                } ${
                  selectedFeedback === "unhappy"
                    ? "grayscale-0 contrast-100"
                    : "grayscale contrast-0 hover:grayscale-0 hover:contrast-100"
                } transition-all duration-300`}
              />
            </Button>
            <p className="mt-2 text-sm">Unhappy</p>
          </div>
        </div>

        {expanded && (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleFinish)} className="space-y-8">
              <FormField
                control={form.control}
                name="items"
                render={() => (
                  <FormItem>
                    <div className="mb-4">
                      <FormLabel className="text-base">{dynamicQuestionText}</FormLabel>
                    </div>
                    {(dynamicOptions && dynamicOptions[selectedFeedback] ? dynamicOptions[selectedFeedback] : []).map((item) => (
                      <FormField
                        key={item.id}
                        control={form.control}
                        name="items"
                        render={({ field }) => {
                          return (
                            <FormItem
                              key={item.id}
                              className="flex flex-row items-start space-x-3 space-y-0 -mt-1"
                            >
                              <FormControl>
                                <Checkbox
                                  checked={field.value?.includes(item.id)}
                                  onCheckedChange={(checked) => {
                                    if (!readOnly) {
                                      return checked
                                        ? field.onChange([...field.value, item.id])
                                        : field.onChange(
                                            field.value?.filter(
                                              (value) => value !== item.id
                                            )
                                          );
                                    }
                                  }}
                                  disabled={readOnly}
                                  className="data-[state=checked]:bg-[#027560] data-[state=checked]:border-[#027560] border-gray-300"
                                  style={{
                                    accentColor: '#027560'
                                  }}
                                />
                              </FormControl>
                              <FormLabel className="text-sm font-normal">
                                {item.label}
                              </FormLabel>
                            </FormItem>
                          );
                        }}
                      />
                    ))}
                    <FormMessage />
                  </FormItem>
                )}
              />
              {showComments && (
                <div>
                  <label htmlFor="comments" className="text-sm font-normal">
                    Would you like to provide further comments?
                  </label>
                  <textarea
                    id="comments"
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    className="w-full p-2 mt-2 border rounded"
                    placeholder="Please enter your comment"
                    disabled={readOnly}
                  />
                </div>
              )}
            </form>
          </Form>
        )}
      </CardContent>
      {selectedFeedback && !readOnly && (
        <CardFooter className="flex justify-between">
          <Button
            className="text-white px-3 py-3 rounded-full text-sm font-medium"
            onClick={() => setShowComments(!showComments)}
            style={{
              borderRadius: '999px',
              backgroundColor: '#027560',
              width: '48%',
              ...(isMobile && {
                fontSize: '14px',
                padding: '12px 16px',
              })
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#025a54';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#027560';
            }}
          >
            {showComments ? "Hide Comments" : "Share Comments"}
          </Button>
          <Button 
            className="text-white px-3 py-3 rounded-full text-sm font-medium"
            disabled={disableSend} 
            onClick={handleFinish}
            style={{
              borderRadius: '999px',
              backgroundColor: disableSend ? '#9ca3af' : '#027560',
              width: '48%',
              ...(isMobile && {
                fontSize: '14px',
                padding: '12px 16px',
              })
            }}
            onMouseEnter={(e) => {
              if (!disableSend) {
                e.target.style.backgroundColor = '#025a54';
              }
            }}
            onMouseLeave={(e) => {
              if (!disableSend) {
                e.target.style.backgroundColor = disableSend ? '#9ca3af' : '#027560';
              }
            }}
          >
            Finish
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};

export default HappinessCard;
